import React, { useEffect, useMemo, useRef, useState } from 'react';
import { mitZeitlimit } from '../../../services/ablageHilfen';
import { baueBauordnungKontext } from '../../../services/aiAssistContext';
import { ladePlanungsphaseFuerKi } from '../../../services/aiAssistPlanung';
import { fetchNotrufplan } from '../../../services/asplanService';
import * as service from '../../../services/bauordnungService';
import type { SchreibKontext } from '../../../services/bauordnungService';
import { describeError } from '../../../services/errorMessage';
import { assistBauordnung } from '../../../services/geminiService';
import { fetchHandbuchGewerke } from '../../../services/handbuchService';
import { renderBauordnungPdf } from '../../../services/pdf/renderBauordnungPdf';
import type { ProjektSteckbrief } from '../../../services/projektSteckbrief/steckbrief';
import type { ReportMetadata, ServiceItem } from '../../../types';
import type { ProjektMerkmale } from '../../planungsphase/types';
import type { NotrufplanInhalt } from '../../sige-plan/types';
import { ablageMeldung, DOKUMENT_ID_SPALTE, erzeugteDokumente } from '../ablage/bauordnungAblage';
import { GEBUCHT_AM_SPALTE } from '../bauordnungBooking';
import type { Hindernis } from '../bauordnungLogic';
import {
    automatikFehlt, baueAutomatikEntwurf, kiMarker, klaerungsRegeln, platzhalterWerte, VERWEIS_ERLASSENDER,
    type AutomatikQuellen,
} from '../bausteine/automatik';
import { baueDokument, bauordnungDateiname } from '../dokument/baueDokument';
import { LAUF_SPERRGRUND, type FreigabeLaufAnsicht } from '../freigabeLauf';
import { DOKUMENTE } from '../kapitel';
import type { BauordnungBundle } from '../types';
import type { HandbuchQuelle } from '../uebernahmeHandbuch';
import type { BauordnungBuchungAnsicht } from '../useBauordnungBuchung';
import type { FreigabeAnstoss } from '../useFreigabeLauf';
import {
    AnlegenKarte, AutomatikKarte, DialogRahmen, ErlassenderKarte, FreigabeKarte, FreigegebenKarte, GruppeKarte,
    HindernisKarte, KiLiestKarte, KlaerungKarte, LadenKarte, NeuDazuKarte, NurAnsichtKarte, UebersichtKarte,
    VorgabenKarte,
} from './DialogKarten';
import type { Eingaben, KopfEingabe } from './dialogKartenTypen';
import {
    fortschritt, kiAuswahl, kiHistorie, kiKontextEingabe, kiRegelZeilen, KOPF_PLATZHALTER, kopfAenderung, LEERE_SITZUNG,
    naechsterSchritt, offeneFragen, ohneUebersprungene, planeAntworten,
    type DialogSchritt, type DialogSitzung, type OffeneFrage,
} from './dialogLogik';

/** Der Reiter, auf den „Alle Bereiche" springt. */
export type AlleBereicheZiel = Hindernis['bereich'] | 'ausgabe';

interface Props {
    projectId: string;
    projektName: string;
    projektNummer?: string;
    projektAdresse?: string;
    bundle: BauordnungBundle;
    merkmale: ProjektMerkmale;
    steckbrief: ProjektSteckbrief | null;
    /** Der Steckbrief liegt fuer GENAU dieses Projekt vor (auch fail-open). */
    steckbriefBereit: boolean;
    /** `undefined`: noch nicht gelesen. */
    handbuch: HandbuchQuelle | undefined;
    /** Schreibweg des Moduls (`useBauordnung`): Schreiben mit Neuladen, Kontext, Stand. */
    b: {
        schreiben: <T>(vorgang: () => Promise<T>) => Promise<T | null>;
        laden: (still?: boolean) => Promise<void>;
        ctx: SchreibKontext;
        arbeitet: boolean;
        fehler: string | null;
    };
    lauf: FreigabeLaufAnsicht;
    /**
     * Freigabe, Journal und Ablage (`useFreigabeLauf`) — und mit `positionId`
     * danach die Buchung dieser Position durch das Modul (E9); der
     * Abschlussdialog bleibt dann zu, oeffnet aber, wenn die Buchung ausfiel.
     */
    freigeben: (a: FreigabeAnstoss, positionId: string | null) => Promise<void>;
    buchung: BauordnungBuchungAnsicht;
    services: ServiceItem[];
    /** Stammdaten fuer den Planungsstand der Freigabe (wie im Reiter „Ausgabe & Freigabe"). */
    metadata: Partial<ReportMetadata> | null;
    stammdatenLaden: boolean;
    /** Eigenes Buero (Profil) — Rolle „SiGeKo Ausführung" und Platzhalter `{sigeko}`. */
    firma: string;
    handelnder: string;
    nurAnsicht: boolean;
    /** Nach „Alle Bereiche" wechseln, auf den passenden Reiter. */
    aufBereich: (ziel?: AlleBereicheZiel) => void;
    /** „Neue Fassung eröffnen" — derselbe Weg wie im Hinweis des Moduls. */
    onNeueFassung?: () => void;
    /** Den Abschlussdialog zur Buchung oeffnen — nur, wenn es dort etwas zu buchen gibt. */
    onBuchen?: () => void;
}

/** Kalendertag der ORTSZEIT — `toISOString()` datierte kurz nach Mitternacht auf gestern. */
const ortsTag = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

type LaufStand = 'bereit' | 'laeuft' | 'fertig' | 'fehler';

/**
 * Der gefuehrte Weg der Baustellenordnung (Plan
 * docs/baustellenordnung-assistenz-plan.md §3, Stufe B2/B3) — beim Oeffnen
 * genau eine Karte. Welche, rechnet `naechsterSchritt` (rein, getestet).
 *
 * ═══ WAS VON SELBST GESCHIEHT ════════════════════════════════════════════
 * Leitentscheid des Betreibers: so automatisiert wie moeglich. Beim
 * Einhaengen, nur im Entwurf und nie in der Nur-Ansicht:
 *   1. Fehlt der Automatik-Marker dieser Fassung, baut sich der Entwurf
 *      (`baueAutomatikEntwurf` → `fuehreAutomatikAus`), sobald Steckbrief,
 *      Handbuch und Notrufplan gelesen sind; danach still `bereinigeDoppelte`
 *      (F17: zwei Sitzungen, zwei Laeufe).
 *   2. Fuehrt das Handbuch inzwischen Ordnungsfelder, die noch nicht
 *      uebernommen sind, holt der Dialog die Uebernahme nach (E7) — der Riegel
 *      `uebernommen_handbuch_am` haelt das bei genau einmal.
 *   3. Fehlt der KI-Marker, liest die KI einmal (abbrechbar); ihre Vorschlaege
 *      werden als unbestaetigte Regeln gespeichert, dann der Marker.
 * Alles andere ist ein Klick des Menschen, und jede Karte sagt, welcher.
 *
 * ═══ SCHREIBEN ════════════════════════════════════════════════════════════
 * Nur ueber `b.schreiben` und den Dienst — derselbe Weg wie „Alle Bereiche",
 * mit Neuladen danach. Die Sitzung („Später", „Weiß nicht", „Stimmt nicht")
 * liegt nur im Speicher; das Modul haengt den Dialog je Projekt neu ein
 * (`key`), so beginnt jedes Projekt mit einer leeren Sitzung.
 *
 * ═══ FREIGABE UND BUCHUNG ═════════════════════════════════════════════════
 * „Freigeben" ist der Lauf des Moduls (Freigabe, Journal, Ablage). Steht der
 * Buchungshaken (E9), liest der Dialog danach den freigegebenen Kopfsatz und
 * bucht genau die eine Position — der Abschlussdialog des Moduls bleibt in
 * diesem Fall zu. Ohne Haken bleibt alles wie im Reiter.
 */
export const BauordnungDialog: React.FC<Props> = ({
    projectId, projektName, projektNummer, projektAdresse, bundle, merkmale, steckbrief, steckbriefBereit, handbuch,
    b, lauf, freigeben, buchung, services, metadata, stammdatenLaden, firma, handelnder, nurAnsicht, aufBereich,
    onNeueFassung, onBuchen,
}) => {
    const [sitzung, setSitzung] = useState<DialogSitzung>(LEERE_SITZUNG);
    const [heuteIso] = useState(ortsTag);
    const o = bundle.ordnung;

    const eingehaengt = useRef(true);
    useEffect(() => {
        eingehaengt.current = true;
        return () => { eingehaengt.current = false; };
    }, []);

    // ─── Quellen der Automatik ───────────────────────────────────────────
    // Der Notrufplan des SiGe-Plans — fail-open und mit Zeitlimit: ohne ihn
    // bleiben Krankenhaus und Durchgangsarzt offene Punkte, sonst nichts. Eine
    // ausbleibende Antwort darf die Automatik nicht fuer immer warten lassen.
    const [notruf, setNotruf] = useState<{ fuer: string; wert: NotrufplanInhalt | null } | null>(null);
    useEffect(() => {
        let gilt = true;
        void mitZeitlimit(fetchNotrufplan(projectId)).catch(() => null).then(wert => { if (gilt) setNotruf({ fuer: projectId, wert }); });
        return () => { gilt = false; };
    }, [projectId]);

    const handbuchWert = handbuch?.wert ?? null;
    const quellen = useMemo<AutomatikQuellen | null>(() => {
        if (!o || o.status !== 'entwurf' || nurAnsicht || o.project_id !== projectId) return null;
        // Erst rechnen, wenn alles gelesen ist — sonst wuerde ein Platzhalter
        // zum offenen Punkt, den das Handbuch eine Sekunde spaeter gefuellt
        // haette. Die Stammdaten gehoeren dazu: aus ihnen kommen Bauherr und
        // Projektname in den Steckbrief.
        if (!steckbriefBereit || stammdatenLaden || handbuch === undefined || notruf?.fuer !== projectId) return null;
        return {
            ordnung: o,
            bundle,
            merkmale,
            steckbrief,
            handbuch: handbuchWert ? { ordnung: handbuchWert.ordnung, org_modell: handbuchWert.org_modell } : null,
            notrufplan: notruf.wert,
            buero: { firma, handelnder },
            projekt: { name: projektName, adresse: projektAdresse ?? '' },
            heuteIso,
        };
    }, [o, bundle, merkmale, steckbrief, steckbriefBereit, stammdatenLaden, handbuch, handbuchWert, notruf, nurAnsicht,
        projectId, firma, handelnder, projektName, projektAdresse, heuteIso]);
    const paket = useMemo(() => (quellen ? baueAutomatikEntwurf(quellen) : null), [quellen]);
    const werte = useMemo(() => (quellen ? platzhalterWerte(quellen) : {}), [quellen]);

    const [automatikStand, setAutomatikStand] = useState<LaufStand>('bereit');
    const [kiLaeuft, setKiLaeuft] = useState(false);

    const lage = {
        projectId,
        bundle,
        merkmale,
        nurAnsicht,
        automatikLaeuft: automatikStand === 'laeuft',
        automatikFehlgeschlagen: automatikStand === 'fehler' || automatikStand === 'fertig',
        kiLaeuft,
        paket,
        werte,
        steckbrief,
        buchung: { darfBuchen: buchung.darfBuchen, services },
        sitzung,
    };
    const schritt = naechsterSchritt(lage);

    // ─── 1. Die Automatik ────────────────────────────────────────────────
    // Von selbst genau einmal je Fassung und Sitzung; scheitert sie — oder
    // meldet Erfolg, ohne dass der Marker zu sehen ist —, steht die Karte mit
    // „Erneut versuchen" da, statt endlos neu zu schreiben (Muster
    // `PlanungDialog`, Katalog). Der Ref sperrt einen zweiten Aufruf, solange
    // der erste laeuft (doppelter Effekt in der Entwicklung).
    const automatikLauf = useRef(false);
    const baueEntwurf = async () => {
        if (automatikLauf.current || !quellen || !o) return;
        automatikLauf.current = true;
        setAutomatikStand('laeuft');
        const paketJetzt = baueAutomatikEntwurf(quellen);
        const ctx = b.ctx;
        const erg = await b.schreiben(async () => {
            const r = await service.fuehreAutomatikAus(o, paketJetzt, ctx);
            // F17: lief in einer zweiten Sitzung derselbe Lauf, stehen Katalogregeln
            // doppelt. Still bereinigen — ein Fehler hier ist kein Fehler des Laufs.
            try {
                await service.bereinigeDoppelte(await service.fetchBauordnungBundle(o.project_id), ctx);
            } catch (e) {
                console.warn('[Bauordnung] Doppelte Regelungen nicht bereinigt:', describeError(e));
            }
            return r;
        });
        automatikLauf.current = false;
        if (eingehaengt.current) setAutomatikStand(erg === null ? 'fehler' : 'fertig');
    };
    useEffect(() => {
        if (schritt.art !== 'automatik_laeuft' || automatikStand !== 'bereit' || !quellen || b.arbeitet) return;
        void baueEntwurf();
        // baueEntwurf liest Quellen und Kontext zum Zeitpunkt des Starts — mehr braucht es nicht.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schritt.art, automatikStand, quellen, b.arbeitet]);
    // Ist der Marker da und die Karte vorbei, darf eine spaetere Fassung derselben
    // Sitzung wieder von selbst laufen. Zustand beim Rendern anpassen (React-Muster).
    if (schritt.art !== 'automatik_laeuft' && automatikStand === 'fertig') setAutomatikStand('bereit');

    // ─── 2. Handbuch nachholen (E7) ──────────────────────────────────────
    const handbuchNachzug = useRef(false);
    const handbuchUebernahme = paket?.handbuch ?? null;
    const ruhig = schritt.art !== 'automatik_laeuft' && schritt.art !== 'ki_liest';
    useEffect(() => {
        if (!o || !handbuchUebernahme || automatikFehlt(o) || !ruhig || b.arbeitet || handbuchNachzug.current) return;
        handbuchNachzug.current = true;
        void b.schreiben(() => service.uebernehmeAusHandbuch(o, handbuchUebernahme, b.ctx, { bestaetigt: true }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [o, handbuchUebernahme, ruhig, b.arbeitet]);

    // ─── 3. Die KI liest von selbst ──────────────────────────────────────
    // Ein Lauf je Fassung (Marker `ki:<Fassung>`), einer zur Zeit. Der Ref
    // haelt den laufenden Aufruf; beim Aushaengen bricht er ab (die Antwort
    // wird verworfen, nichts geschrieben) — so startet ein erneutes
    // Einhaengen wieder (Muster `PlanungDialog`).
    const kiLauf = useRef<AbortController | null>(null);
    useEffect(() => () => {
        const laufend = kiLauf.current;
        kiLauf.current = null;
        laufend?.abort();
    }, []);

    const liesKi = async () => {
        if (!o || kiLauf.current) return;
        const ctrl = new AbortController();
        kiLauf.current = ctrl;
        setKiLaeuft(true);
        const ctx = b.ctx;
        const kiAus = (kiFehler: string) => {
            if (!ctrl.signal.aborted && eingehaengt.current) setSitzung(x => ({ ...x, kiAus: true, kiFehler }));
        };
        try {
            const [planung, gewerke] = await Promise.all([
                ladePlanungsphaseFuerKi(projectId),
                handbuchWert
                    ? fetchHandbuchGewerke(handbuchWert.id)
                        .then(g => g.map(x => String(x.gewerk_label || '').trim()).filter(Boolean))
                        .catch(() => [] as string[])
                    : Promise.resolve([] as string[]),
            ]);
            if (ctrl.signal.aborted) return;
            const globalData = metadata?.globalData;
            const kontext = baueBauordnungKontext(kiKontextEingabe({
                bundle,
                merkmale,
                projekt: { bauvorhaben: projektName, bauzeitVon: globalData?.startDate, bauzeitBis: globalData?.endDate },
                planung,
                handbuch: handbuchWert,
                gewerke,
            }));
            const erg = await assistBauordnung(kontext);
            if (ctrl.signal.aborted) return;
            if (!erg.ok) {
                kiAus(erg.fehler || 'Die KI hat nicht geantwortet.');
                return;
            }
            const zeilen = kiRegelZeilen(erg.daten.vorschlaege, bundle.regeln);
            // Insert UND Marker in einem Dienstaufruf: der liest den Kopfsatz
            // frisch und schreibt nichts, wenn eine zweite Sitzung den Lauf
            // schon abgeschlossen hat (Codex-Review 02.11.2026).
            const gespeichert = await b.schreiben(() => service.fuegeKiVorschlaegeEin(
                o, zeilen, ctx, kiHistorie(kiMarker(o), zeilen.length, erg.verworfeneVorschlaege),
            ));
            if (gespeichert === null) kiAus('Die Vorschläge der KI ließen sich nicht speichern.');
        } catch (e) {
            kiAus(describeError(e));
        } finally {
            // Nur der juengste Lauf gibt die Sperre frei — ein beim Aushaengen
            // abgebrochener hat den Ref schon geleert.
            if (kiLauf.current === ctrl) {
                kiLauf.current = null;
                setKiLaeuft(false);
            }
        }
    };
    useEffect(() => {
        if (schritt.art !== 'ki_liest' || kiLaeuft || kiLauf.current || b.arbeitet) return;
        void liesKi();
        // liesKi liest den Stand zum Zeitpunkt des Starts — mehr braucht es nicht.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schritt.art, kiLaeuft, b.arbeitet]);

    // ─── Antworten ───────────────────────────────────────────────────────

    const mitEintrag = <T,>(menge: ReadonlySet<T>, wert: T): Set<T> => new Set(menge).add(wert);
    const umschalten = <T,>(menge: ReadonlySet<T>, wert: T): Set<T> => {
        const neu = new Set(menge);
        if (neu.has(wert)) neu.delete(wert); else neu.add(wert);
        return neu;
    };

    /** Die Antworten einer Karte — je Frage ein Aufruf, in EINEM Schreibvorgang (ein Neuladen). */
    const beantworte = (fragen: readonly OffeneFrage[], eingaben: Eingaben) => {
        const schritte = planeAntworten(fragen, eingaben, bundle.regeln);
        if (!schritte.length) return;
        const ctx = b.ctx;
        void b.schreiben(async () => {
            for (const s of schritte) await service.beantworteLuecken(s.luecken, s.regeln, s.antwort, ctx);
            return true;
        });
    };

    /** „Regelung weglassen": alle Regeln der Frage, mit Abwahl-Vermerk; Punkte ohne Regel werden mit erledigt. */
    const weglassen = (f: OffeneFrage) => {
        if (!o) return;
        const ohneRegel = f.luecken.filter(l => !l.regel_id || !f.regeln.some(r => r.id === l.regel_id)).map(l => l.id);
        const ctx = b.ctx;
        void b.schreiben(() => service.loescheRegelnMitAbwahl(f.regeln, o, ctx, ohneRegel));
    };

    const stimmtSoUebersicht = async (s: Extract<DialogSchritt, { art: 'uebersicht' }>) => {
        const { bestaetigen, loeschen } = kiAuswahl(s.uebersicht);
        if (bestaetigen.length || loeschen.length) {
            const ctx = b.ctx;
            const ok = await b.schreiben(async () => {
                await service.bestaetigeRegeln(bestaetigen, ctx);
                await service.loescheRegeln(loeschen, ctx);
                return true;
            });
            if (ok === null) return;
        }
        if (eingehaengt.current) setSitzung(x => ({ ...x, uebersichtGesehen: s.schluessel, verworfeneKi: new Set() }));
    };

    const kopfSpeichern = (k: NonNullable<Extract<DialogSchritt, { art: 'hindernis' }>['kopf']>, e: KopfEingabe) => {
        if (!o) return;
        const { patch, eingaben, erlassenderErledigt } = kopfAenderung(o, k, e);
        const fragen = offeneFragen(bundle).filter(f => KOPF_PLATZHALTER.has(f.name));
        const schritte = planeAntworten(fragen, eingaben, bundle.regeln);
        const kopfLuecken = erlassenderErledigt ? k.luecken.filter(l => l.verweis === VERWEIS_ERLASSENDER) : [];
        const ctx = b.ctx;
        void b.schreiben(async () => {
            if (Object.keys(patch).length) await service.updateOrdnung(o.id, patch, ctx);
            for (const s of schritte) await service.beantworteLuecken(s.luecken, s.regeln, s.antwort, ctx);
            for (const l of kopfLuecken) await service.updateLuecke(l.id, { erledigt_am: heuteIso }, ctx);
            return true;
        });
    };

    const pdfAnsehen = () => {
        if (!o) return;
        for (const art of erzeugteDokumente(o)) {
            const dok = baueDokument(bundle, art, { projektName, projektNummer, projektAdresse, merkmale });
            const pdf = renderBauordnungPdf(dok, { fusszeileLinks: o.erlassender_firma });
            pdf.save(bauordnungDateiname(art, projektName || 'Projekt', o.stand));
        }
    };

    /**
     * Freigabe, danach (nur mit Haken) die Buchung der EINEN Position. Beides
     * fuehrt das MODUL aus (`freigeben`): es bleibt eingehaengt, auch wenn der
     * Mensch waehrenddessen nach „Alle Bereiche" wechselt und diese Karte
     * verschwindet — sonst bliebe die Buchung aus, und der Abschlussdialog
     * waere schon unterdrueckt (Codex-Review 02.11.2026).
     */
    const freigabe = async (s: Extract<DialogSchritt, { art: 'freigabe' }>, fassung: string, buchen: boolean) => {
        if (!o) return;
        const position = buchen && s.haken.art === 'haken' ? s.haken.position : null;
        await freigeben(
            { bundle, merkmale, projektName, projektNummer, projektAdresse, ctx: b.ctx, fassung, metadata },
            position?.id ?? null,
        );
        if (eingehaengt.current) await b.laden(true);
    };

    // ─── Karte ───────────────────────────────────────────────────────────

    const schreibstand = { laeuft: b.arbeitet, fehler: b.fehler };

    const karte = (): React.ReactNode => {
        switch (schritt.art) {
            case 'laden':
                return <LadenKarte />;
            case 'anlegen':
                return (
                    <AnlegenKarte
                        nurAnsicht={schritt.nurAnsicht}
                        {...schreibstand}
                        onAnlegen={() => {
                            const ctx = b.ctx;
                            void b.schreiben(() => service.ensureBauordnung(projectId, ctx, { geltung_ort: projektAdresse || '' }));
                        }}
                    />
                );
            case 'automatik_laeuft':
                return (
                    <AutomatikKarte
                        fehlgeschlagen={schritt.fehlgeschlagen}
                        fehler={b.fehler}
                        onErneut={() => setAutomatikStand('bereit')}
                        onOhne={() => setSitzung(x => ({ ...x, automatikAus: true }))}
                    />
                );
            case 'ki_liest':
                return (
                    <KiLiestKarte
                        onAbbrechen={() => {
                            const laufend = kiLauf.current;
                            kiLauf.current = null;
                            laufend?.abort();
                            setKiLaeuft(false);
                            setSitzung(x => ({ ...x, kiAus: true }));
                        }}
                    />
                );
            case 'freigegeben': {
                if (!o) return null;
                const art = o.erzeugt_ordnung ? 'ordnung' : 'hse';
                return (
                    <FreigegebenKarte
                        fassung={String(o.version_label ?? '').trim()}
                        am={o.freigegeben_am}
                        von={o.freigegeben_von_name}
                        dokumente={erzeugteDokumente(o).map(d => ({ titel: DOKUMENTE[d].titel, abgelegt: !!o[DOKUMENT_ID_SPALTE[d]] }))}
                        ablageLaeuft={lauf.laeuft}
                        ablageMeldung={lauf.ablage ? ablageMeldung(lauf.ablage) : null}
                        gebuchtAm={o[GEBUCHT_AM_SPALTE[art]] ?? null}
                        buchungsMeldung={buchung.meldung}
                        nurAnsicht={nurAnsicht}
                        sperre={lauf.laeuft ? LAUF_SPERRGRUND : (b.arbeitet || buchung.laeuft) ? 'Es wird gerade gespeichert.' : null}
                        onNeueFassung={nurAnsicht ? undefined : onNeueFassung}
                        onBuchen={nurAnsicht ? undefined : onBuchen}
                    />
                );
            }
            case 'nur_ansicht':
                return <NurAnsichtKarte zusammen={schritt.zusammen} />;
            case 'uebersicht':
                return (
                    <UebersichtKarte
                        uebersicht={schritt.uebersicht}
                        {...schreibstand}
                        onUmschalten={id => setSitzung(x => ({ ...x, verworfeneKi: umschalten(x.verworfeneKi, id) }))}
                        onStimmtSo={() => { void stimmtSoUebersicht(schritt); }}
                    />
                );
            case 'neu_dazu':
                return (
                    <NeuDazuKarte
                        zeilen={schritt.zeilen}
                        {...schreibstand}
                        onStimmtSo={() => {
                            if (!o || !paket) return;
                            const ctx = b.ctx;
                            void b.schreiben(() => service.fuehreAutomatikAus(o, paket, ctx));
                        }}
                        onWeglassen={() => {
                            if (!o) return;
                            const ids = schritt.zeilen.map(z => z.bausteinId);
                            const ctx = b.ctx;
                            void b.schreiben(() => service.waehleBausteineAb(o, ids, ctx));
                        }}
                        onSpaeter={() => setSitzung(x => ({ ...x, neuDazuSpaeter: true }))}
                    />
                );
            case 'erlassender': {
                const luecke = schritt.luecke;
                return (
                    <ErlassenderKarte
                        wahl={schritt.wahl}
                        {...schreibstand}
                        onWaehle={w => {
                            if (!o) return;
                            const fragen = offeneFragen(bundle).filter(f => f.name === 'erlassender');
                            const schritte = planeAntworten(fragen, { 'platzhalter:erlassender': w.firma }, bundle.regeln);
                            const ctx = b.ctx;
                            void b.schreiben(async () => {
                                await service.updateOrdnung(o.id, { ...w.patch, erlassender_firma: w.firma }, ctx);
                                await service.updateLuecke(luecke.id, { erledigt_am: heuteIso }, ctx);
                                for (const s of schritte) await service.beantworteLuecken(s.luecken, s.regeln, s.antwort, ctx);
                                return true;
                            });
                        }}
                        onAndere={() => setSitzung(x => ({ ...x, erlassenderVonHand: true }))}
                    />
                );
            }
            case 'klaerung': {
                const { luecke, merkmal } = schritt;
                return (
                    <KlaerungKarte
                        key={luecke.id}
                        frage={schritt.frage}
                        kurz={schritt.kurz}
                        bausteine={schritt.bausteine}
                        offen={schritt.offen}
                        laeuft={b.arbeitet || !quellen}
                        fehler={b.fehler}
                        onAntwort={ja => {
                            if (!o || !quellen) return;
                            const regeln = klaerungsRegeln(quellen, merkmal, ja);
                            const ctx = b.ctx;
                            void b.schreiben(() => service.klaerungBeantworten(o, luecke, ja, regeln, ctx));
                        }}
                        onWeissNicht={() => setSitzung(x => ({ ...x, klaerungSpaeter: mitEintrag(x.klaerungSpaeter, luecke.id) }))}
                    />
                );
            }
            case 'vorgaben':
                return (
                    <VorgabenKarte
                        key="vorgaben"
                        fragen={schritt.fragen}
                        {...schreibstand}
                        onStimmtSo={eingaben => beantworte(schritt.fragen, eingaben)}
                        onWeglassen={weglassen}
                        onSpaeter={() => setSitzung(x => ({ ...x, vorgabenSpaeter: true }))}
                    />
                );
            case 'luecken_gruppe': {
                const gruppe = schritt.gruppe;
                return (
                    <GruppeKarte
                        key={`gruppe:${gruppe}`}
                        gruppe={gruppe}
                        label={schritt.label}
                        fragen={schritt.fragen}
                        vorbelegung={schritt.vorbelegung}
                        {...schreibstand}
                        onWeiter={eingaben => beantworte(schritt.fragen, eingaben)}
                        onWeglassen={weglassen}
                        onSpaeter={() => setSitzung(x => ({ ...x, gruppenSpaeter: mitEintrag(x.gruppenSpaeter, gruppe) }))}
                    />
                );
            }
            case 'hindernis': {
                const k = schritt.kopf;
                return (
                    <HindernisKarte
                        key={`hindernis:${k ? `${k.firma.zeigen}:${k.name.zeigen}:${k.firma.wert}:${k.name.wert}` : '-'}`}
                        kopf={k}
                        uebersprungen={schritt.uebersprungen}
                        rest={schritt.rest}
                        kannWiederZeigen={schritt.kannWiederZeigen}
                        {...schreibstand}
                        onKopf={e => { if (k) kopfSpeichern(k, e); }}
                        onAlleBereiche={ziel => aufBereich(ziel)}
                        onWiederZeigen={() => setSitzung(ohneUebersprungene)}
                    />
                );
            }
            case 'freigabe': {
                const sperrgrund = stammdatenLaden
                    ? 'Die Stammdaten des Projekts werden noch gelesen. Ohne sie kann der Planungsstand der Freigabe nicht festgehalten werden.'
                    : lauf.laeuft ? LAUF_SPERRGRUND : null;
                return (
                    <FreigabeKarte
                        key={`freigabe:${schritt.haken.art}`}
                        zusammen={schritt.zusammen}
                        fassung={schritt.fassung}
                        haken={schritt.haken}
                        sperrgrund={sperrgrund}
                        laeuft={b.arbeitet || !!buchung.laeuft}
                        fehler={b.fehler}
                        onPdf={pdfAnsehen}
                        onFreigeben={(fassung, buchen) => { void freigabe(schritt, fassung, buchen); }}
                    />
                );
            }
        }
    };

    return (
        <DialogRahmen
            fortschritt={fortschritt(schritt, lage)}
            fuss={(
                <span>
                    Alles andere steht unter „Alle Bereiche“.{' '}
                    <button type="button" onClick={() => aufBereich()} className="font-bold text-blue-700 hover:underline">
                        Öffnen
                    </button>
                </span>
            )}
        >
            {karte()}
        </DialogRahmen>
    );
};
